import React, { useContext, useState, useMemo } from 'react';

import { dashboardCompanyApis } from '@apis/next/companyApis';
import { Button } from '@components/Button';
import { ActionableDialog, ActionableDialogTitle } from '@components/Dialog';
import { TextInput } from '@components/NextForms/TextInput';
import { allCompanyGrades, companyStrings } from '@data/company';
import { routePlanServiceNameStrings, routePlanStrings } from '@data/routePlan';
import { useDialogKey } from '@hooks/useDialogKey';
import { useFormUtils } from '@hooks/useFormUtils';
import {
  CompanyCustomerServiceGrade,
  CompanyCustomerServiceGradeForm,
  CompanyHelper,
  DashboardPatchCompany,
} from '@models/next/Company';
import { ServiceName } from '@models/next/RoutePlan';
import { mapDecodeResult } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { MemberDetailsContext } from '@pages/admin/members/helpers';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { formatDate, recordToKV } from '@utils/types';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';

const strings = createStrings({
  ja: {
    updateGrades: 'グレードを変更する',
    customerServiceGradesUpdated: (name: ServiceName) =>
      `${routePlanServiceNameStrings[name]}の航路サービスグレードが更新されました`,
    customerServiceGradesWillUpdate: (name: ServiceName) =>
      `${routePlanServiceNameStrings[name]}の航路サービスグレードの更新が予定されました`,
    errorUpdateDateBeforeTomorrow: '過去の日付は無効です。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  companyCustomerServiceGradesForm: {
    ...common.middleText,
    ...common.textGray700,
  },
  companyCustomerServiceGradesDisplay: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    rowGap: 6,
  },
  companyCustomerServiceGradesContainer: {
    ...common.flexCenter,
    paddingTop: '1rem',
    paddingBottom: '0.5rem',
  },
  companyCustomerServiceGradesFormDialogForm: {
    ...common.middleText,
    ...common.textGray700,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr auto',
    columnGap: 10,
    rowGap: 10,
  },
  companyCustomerSerivceGradesDialogContent: {
    padding: '50px 100px',
    '& *[role="data"]': {
      paddingTop: 5,
    },
  },
}));

const CompanyCustomerServiceGradesFormLine: React.FC<{
  name: ServiceName;
  onSubmit: (body: DashboardPatchCompany) => Promise<void>;
}> = observer(({ name, onSubmit }) => {
  const { companyDetails } = useContext(MemberDetailsContext);

  const initialOutput = useMemo(
    () => CompanyHelper.mapDetailsToCustomerServiceGradesForm(companyDetails, name),
    [companyDetails],
  );

  const form = useForm(CompanyCustomerServiceGradeForm, {
    initialOutput,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  return (
    <form
      style={{ display: 'contents' }}
      {...form.bindForm({
        async onSubmit(values) {
          if (
            values.next_grade_update_date &&
            dayjs(values.next_grade_update_date).isBefore(dayjs().startOf('day'))
          ) {
            fields.next_grade_update_date.setExtraErrorMessages([
              strings.errorUpdateDateBeforeTomorrow,
            ]);
            return;
          }

          await onSubmit({
            customer_service_grades: {
              ...companyDetails.customer_service_grades,
              [name]: {
                ...values,
                grade:
                  !values.next_grade_update_date ||
                  dayjs(values.next_grade_update_date).isSame(dayjs(), 'day')
                    ? values.next_grade
                    : values.grade,
              } satisfies CompanyCustomerServiceGrade,
            },
          });
        },
      })}
    >
      <div role="data">{routePlanServiceNameStrings[name]}</div>
      <div role="data">
        {mapDecodeResult(fields.grade.decodeResult, (grade) => allCompanyGrades[grade], 'ERROR')}
      </div>
      <div>
        <TextInput
          field={fields.next_grade}
          inputOptions={{
            type: 'choose',
            options: allCompanyGrades.map((label, value) => ({
              label,
              value,
            })),
          }}
        />
      </div>
      <div>
        <TextInput
          field={fields.next_grade_update_date}
          inputOptions={{
            type: 'date',
            keyboardDatePickerProps: {
              minDate: formatDate(dayjs()),
            },
          }}
        />
      </div>
      <div>
        <Button color="primary" shape="round" size="small" text={formStrings.update} />
      </div>
    </form>
  );
});

const CompanyCustomerServiceGradesFormDialog: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { companyDetails } = useContext(MemberDetailsContext);

  const { customer_service_grades } = companyDetails;

  const queryClient = useQueryClient();

  const patchCompany = dashboardCompanyApis.usePatch(
    '/dashboard/company/:id/detail',
    {
      params: {
        id: companyDetails.id,
      },
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(
          dashboardCompanyApis.getKeyByPath('get', '/dashboard/company/:id/detail'),
        );
      },
    },
  );

  const [successMessage, setSuccessMessage] = useState<string>();

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [patchCompany],
    formAlertProps: {
      showSuccess: true,
      successMessage,
    },
  });

  return (
    <ActionableDialog open={open} onClose={onClose} size="lg">
      <ActionableDialogTitle textAlign="center">
        {companyStrings.customer_service_grades}
      </ActionableDialogTitle>

      <div className={classes.companyCustomerSerivceGradesDialogContent}>
        {renderAlertsAndSpinnerModal()}

        <div className={classes.companyCustomerServiceGradesFormDialogForm}>
          <div>{routePlanStrings.serviceName}</div>
          <div>{companyStrings.grade}</div>
          <div>{companyStrings.next_grade}</div>
          <div>{companyStrings.next_grade_update_date}</div>
          <div />

          {recordToKV(customer_service_grades).map(([service_name]) => (
            <CompanyCustomerServiceGradesFormLine
              key={service_name}
              name={service_name}
              onSubmit={async (values) => {
                if (values.customer_service_grades[service_name].next_grade_update_date) {
                  setSuccessMessage(strings.customerServiceGradesWillUpdate(service_name));
                } else {
                  setSuccessMessage(strings.customerServiceGradesUpdated(service_name));
                }
                await patchCompany.mutateAsync(values);
              }}
            />
          ))}
        </div>
      </div>
    </ActionableDialog>
  );
};

export const CompanyCustomerServiceGradesForm: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { companyDetails } = useContext(MemberDetailsContext);

  const { customer_service_grades } = companyDetails;

  const [dialogOpen, setDialogOpen] = useState(false);

  const dialogKey = useDialogKey(dialogOpen);

  return (
    <div className={classes.companyCustomerServiceGradesForm}>
      <CompanyCustomerServiceGradesFormDialog
        key={dialogKey}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />

      <div className={classes.companyCustomerServiceGradesDisplay}>
        {recordToKV(customer_service_grades).map(([service_name, grades]) => (
          <React.Fragment key={service_name}>
            <div>{routePlanServiceNameStrings[service_name]}</div>
            <div>{allCompanyGrades[grades.grade]}</div>
          </React.Fragment>
        ))}
      </div>

      <div className={classes.companyCustomerServiceGradesContainer}>
        <Button
          color="primary"
          shape="round"
          size="small"
          text={strings.updateGrades}
          style={{ width: 160 }}
          onClick={() => setDialogOpen(true)}
        />
      </div>
    </div>
  );
};
